import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, inject, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { PrefixIconComponent } from 'components/layout/prefix-icon/prefix-icon.component';
import { Highlighter } from 'components/shared/highlighter/highlighter';
import { AutocompleteOffDirective } from 'directives/autocomplete-off.directive';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { CompanySearchResultViewModel, SearchResultMatchValueModel } from 'models/generated/Models.CompanySearchResultView';
import { Layout } from 'modules/layout';
import { Pipes } from 'modules/pipes';
import { map, combineLatest, Observable, Subscription, switchMap } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CompanyService } from 'services/company.service';
import { DomainService } from 'services/domain.service';


@Component({
	selector: 'company-search',
	templateUrl: './company-search.component.html',
	styleUrl: './company-search.component.scss',
	imports: [Layout, Pipes, CustomInputComponent, MatFormField, MatLabel, InputStateDirective, MatInput, FormsModule, ValidationDirective, AutocompleteOffDirective, MatIcon, MatSuffix, PrefixIconComponent, NgClass, Highlighter]
})
export class CompanySearchComponent {

	public query = '';
	private matchingResults: CompanySearchResultViewModel[] = [];
	public visibleResults: CompanySearchResultViewModel[] = [];
	public highlightedResult?: CompanySearchResultViewModel;
	private lastVisibleIndex = 0;

	public pendingSearch?: Subscription;
	private currentSearch: { base: string, results: CompanySearchResultViewModel[] } = {
		base: '',
		results: []
	};
	private customerNoSearch?: CompanySearchResultViewModel;

	@ViewChild('searchResultContainer') searchResultContainer?: ElementRef<HTMLDivElement>;
	@ViewChild('input') inputElement?: ElementRef<HTMLInputElement>;
	@Input() displayVatNumber: boolean = false;
	@Input() set focus(value: boolean) {
		if (value) setTimeout(() => {
			if (this.inputElement) this.inputElement.nativeElement.select();
		});
	}
	@Output() onClose = new EventEmitter();
	@Output() onSelected = new EventEmitter<string>();

	private readonly companyService = inject(CompanyService);
	private readonly accountingService = inject(AccountingService);
	private readonly domainService = inject(DomainService);

	private _includeInactiveCompanies: boolean = (localStorage['companySearch.includeInactiveCompanies'] == 'true');
	public set includeInactiveCompanies(value: boolean) {
		localStorage['companySearch.includeInactiveCompanies'] = this._includeInactiveCompanies = value;
		this.filterResults();
	}
	public get includeInactiveCompanies() { return this._includeInactiveCompanies; }

	public queryChanged() {
		let query = this.query.trim();
		
		if (query.length < 2) {
			this.visibleResults = [];
			return;
		}

		this.customerNoSearch = undefined;
		if (query.length >= 5) {
			this.searchByCustomerNo(query);
		}

		if (this.currentSearch.base !== '' && query.substring(0, this.currentSearch.base.length) === this.currentSearch.base) {
			// There is already a filtered or pending search based on the same "base" query. No reason to start a new one, just filter the existing results
			if (this.currentSearch.results.length > 0) this.filterResults();
			return;
		}

		if (this.pendingSearch) this.pendingSearch.unsubscribe();
		this.currentSearch.base = query.substring(0, 4);
		this.currentSearch.results = [];
		this.visibleResults = [];

		const domainSearch = this.searchByDomain(this.currentSearch.base);
		const companyNameSearch = this.companyService.searchCompaniesByKeyword(this.currentSearch.base);

		this.pendingSearch = combineLatest([companyNameSearch, domainSearch])
			.subscribe((resultSets) => {
				// Unless an observable gets pipe(startsWith()), subscription will only fire when all the observables have returned a result
				// This is intentional here, as we don't want results to pop in over time
				this.currentSearch.results = resultSets.flatMap(results => results ?? []);
				this.filterResults();
				this.pendingSearch = undefined;
			});
	}

	private searchByDomain(query: string): Observable<CompanySearchResultViewModel[]> {
		return this.domainService.searchDomains(query).pipe(switchMap(domains =>
			this.companyService.searchCompaniesByProductPlanIds(domains.map(d => d.companyId!).filter(id => id), domains.map(d => d.productPlanId!).filter(id => id))
				.pipe(map((results) => {
					for (const result of results)
						result.matchValues = domains
							.filter(d => result.companyId == d.companyId || result.matchingProductPlanIds.some(pid => pid == d.productPlanId))
							.map(d => Object.assign(new SearchResultMatchValueModel(), { value: d.name }));
							
					return results;
				}))
		));
	}

	private filterResults() {
		let query = this.query.trim().toLowerCase();
		const matchingResults: CompanySearchResultViewModel[] = [];
		for (const result of this.currentSearch.results) {
			// If customerNo search returned a result, skip other matches on the same company (some companies have customerno == phonenumber)
			if (this.customerNoSearch && result.companyId === this.customerNoSearch.companyId) continue;

			if (this.matchValue(result.companyName) || (result.matchValues && result.matchValues.some(v => this.matchValue(v.value, v.type)))) {

				if (!result.hasActivePlans && !this.includeInactiveCompanies) continue;

				const existingResult = matchingResults.find(r => r.companyId == result.companyId);
				if (!existingResult) matchingResults.push(result);
				else existingResult.matchValues.push(...result.matchValues.filter(mv1 => !existingResult.matchValues.some(mv2 => mv1.value == mv2.value && mv1.type == mv2.type)));
			}
		}
		const matchesStart = (value: string) => value.toLowerCase().startsWith(query);
		this.matchingResults = matchingResults
			.sort((a, b) => {
				if (matchesStart(a.companyName) && !matchesStart(b.companyName))
					return -1;
				if (matchesStart(b.companyName) && !matchesStart(a.companyName))
					return 1;

				if (a.matchValues.some(v => matchesStart(v.value)) && !b.matchValues.some(v => matchesStart(v.value)))
					return -1;
				if (b.matchValues.some(v => matchesStart(v.value)) && !a.matchValues.some(v => matchesStart(v.value)))
					return 1;
				
				return a.companyName.localeCompare(b.companyName);
			});

		this.visibleResults = this.matchingResults.slice(0, 80);
		this.lastVisibleIndex = 80;
		if (this.customerNoSearch) this.visibleResults.unshift(this.customerNoSearch);		
	}

	private async searchByCustomerNo(customerNo: string) {
		const settings = await this.accountingService.findCompanySettingsByCustomerNo(customerNo);
		if (settings.length == 0) return;

		for (const setting of settings) {
			const company = await this.companyService.getCompany(setting.companyId!);
			if (!company) continue;

			this.customerNoSearch = new CompanySearchResultViewModel();
			this.customerNoSearch.companyId = company.id;
			this.customerNoSearch.companyName = company.name;
			this.customerNoSearch.matchValues.push(Object.assign(new SearchResultMatchValueModel(), { value: setting.customerNo }));
			this.filterResults();
		}
	}

	public scrolledResults() {
		if (!this.searchResultContainer || this.lastVisibleIndex >= this.matchingResults.length) return;

		const containerElement = this.searchResultContainer.nativeElement;
		const scrollBottom = containerElement.scrollTop + containerElement.offsetHeight;
		if (scrollBottom < containerElement.scrollHeight - 100) return;

		this.visibleResults.push(...this.matchingResults.slice(this.lastVisibleIndex, this.lastVisibleIndex+80));
		this.lastVisibleIndex += 80;
	}

	public matchValue(value: string, type: string = 'text') {
		switch (type) {
			case 'phone':
				const sanitized = this.query.replace(/\W/g, '');
				return value.toLowerCase().substring(0, sanitized.length) === sanitized.toLowerCase();
			default:
				return value.toLowerCase().indexOf(this.query.toLowerCase()) >= 0;
		}
	}

	@HostListener('keydown', ['$event.keyCode'])
	public pressedKey(keyCode: number): boolean {
		switch (keyCode) {
			case 38: // UP
				this.changeSelectedIndex(-1);
				return false;
			case 40: // DOWN
				this.changeSelectedIndex(1);
				return false;
			case 13: // ENTER
				if (this.highlightedResult) this.selectCompany(this.highlightedResult);
				return false;
			case 27: // ESCAPE
				this.onClose.emit();
				return false;
		}
		return true;
	}
	private changeSelectedIndex(offset: number) {
		let currentIndex = this.highlightedResult ? this.visibleResults.indexOf(this.highlightedResult) : -1;
		currentIndex += offset;
		if (currentIndex < 0) currentIndex = this.visibleResults.length - 1;
		if (currentIndex >= this.visibleResults.length) currentIndex = 0;

		this.highlightedResult = this.visibleResults[currentIndex];
	}
	public selectCompany(result: CompanySearchResultViewModel) {
		this.onSelected.emit(result.companyId);
		this.onClose.emit();
	}
}
